/**
 * Keyboard shortcut disclosure primitives.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useState } from "react";
import { GRAY, PRIMARY } from "../design-system/index.js";
import { useDocumentKeyDown } from "../hooks/index.js";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "./primitives/index.js";

export interface KeyboardShortcut {
  readonly keys: readonly string[] | string;
  readonly label: string;
  readonly group?: string;
}

export interface KeyboardShortcutsDialogProps {
  readonly shortcuts: readonly KeyboardShortcut[];
  readonly title?: string;
  readonly description?: string;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly triggerLabel?: string;
}

export interface KeyboardShortcutHintsProps {
  readonly shortcuts: readonly KeyboardShortcut[];
  readonly maxItems?: number;
}

function keysForDisplay(keys: readonly string[] | string): readonly string[] {
  return typeof keys === "string" ? [keys] : keys;
}

function shortcutGroups(
  shortcuts: readonly KeyboardShortcut[],
): readonly [string, readonly KeyboardShortcut[]][] {
  const groups = new Map<string, KeyboardShortcut[]>();
  for (const shortcut of shortcuts) {
    const key = shortcut.group ?? "Shortcuts";
    const current = groups.get(key);
    if (current === undefined) groups.set(key, [shortcut]);
    else current.push(shortcut);
  }
  return [...groups.entries()];
}

function KeyChord({ keys }: { readonly keys: readonly string[] | string }): React.JSX.Element {
  return (
    <span style={{ display: "inline-flex", gap: 4, whiteSpace: "nowrap" }}>
      {keysForDisplay(keys).map((key) => (
        <kbd
          key={key}
          style={{
            background: GRAY[100],
            border: `1px solid ${GRAY[300]}`,
            borderRadius: 5,
            color: GRAY[700],
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "0.75rem",
            fontWeight: 700,
            lineHeight: 1,
            minWidth: 24,
            padding: "0.25rem 0.4rem",
            textAlign: "center",
          }}
        >
          {key}
        </kbd>
      ))}
    </span>
  );
}

export function KeyboardShortcutsDialog({
  shortcuts,
  title = "Keyboard shortcuts",
  description = "Available keyboard commands for this demo surface.",
  open,
  onOpenChange,
  triggerLabel = "Keyboard shortcuts",
}: KeyboardShortcutsDialogProps): React.JSX.Element {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const actualOpen = open ?? internalOpen;
  const setOpen = useCallback(
    (nextOpen: boolean): void => {
      if (!isControlled) setInternalOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  useDocumentKeyDown(
    (event) => {
      if (event.key !== "?") return;
      event.preventDefault();
      setOpen(true);
    },
    { enabled: shortcuts.length > 0 },
  );

  return (
    <Dialog open={actualOpen} onOpenChange={setOpen}>
      <button
        type="button"
        aria-label={triggerLabel}
        onClick={() => {
          setOpen(true);
        }}
        style={{
          alignItems: "center",
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: 6,
          color: GRAY[300],
          cursor: "pointer",
          display: "inline-flex",
          fontSize: "0.8125rem",
          fontWeight: 700,
          height: 30,
          justifyContent: "center",
          width: 30,
        }}
      >
        ?
      </button>
      <DialogContent aria-label={title}>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
        <div style={{ display: "grid", gap: 16, marginTop: 18 }}>
          {shortcutGroups(shortcuts).map(([group, groupShortcuts]) => (
            <section key={group}>
              <h3
                style={{
                  color: PRIMARY[700],
                  fontSize: "0.75rem",
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  margin: "0 0 0.5rem",
                  textTransform: "uppercase",
                }}
              >
                {group}
              </h3>
              <dl style={{ display: "grid", gap: 10, margin: 0 }}>
                {groupShortcuts.map((shortcut) => (
                  <div
                    key={`${group}:${shortcut.label}`}
                    style={{
                      alignItems: "center",
                      display: "grid",
                      gap: 12,
                      gridTemplateColumns: "minmax(5rem, auto) 1fr",
                    }}
                  >
                    <dt>
                      <KeyChord keys={shortcut.keys} />
                    </dt>
                    <dd style={{ color: GRAY[700], margin: 0 }}>{shortcut.label}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
        <DialogClose asChild>
          <button
            type="button"
            style={{
              background: PRIMARY[700],
              border: 0,
              borderRadius: 6,
              color: "white",
              cursor: "pointer",
              fontSize: "0.8125rem",
              fontWeight: 700,
              marginTop: 18,
              padding: "0.5rem 0.75rem",
            }}
          >
            Close
          </button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}

export function KeyboardShortcutHints({
  shortcuts,
  maxItems = 4,
}: KeyboardShortcutHintsProps): React.JSX.Element | null {
  if (shortcuts.length === 0) return null;
  return (
    <div
      aria-label="Keyboard shortcut hints"
      style={{
        alignItems: "center",
        color: GRAY[500],
        display: "flex",
        flexWrap: "wrap",
        fontSize: "0.75rem",
        gap: 8,
      }}
    >
      {shortcuts.slice(0, maxItems).map((shortcut) => (
        <span key={shortcut.label} style={{ alignItems: "center", display: "inline-flex", gap: 6 }}>
          <KeyChord keys={shortcut.keys} />
          <span>{shortcut.label}</span>
        </span>
      ))}
    </div>
  );
}
